import { scriptLoadAY } from '../utils/ad-ops/scriptLoadAY'
import debug from '../utils/console'
import type {
  AnalyticsProps,
  AssertiveYieldOptions,
  Slot,
} from '../types/assertive'
import type { RouteLocationNormalizedGeneric } from 'vue-router'
import { OpenFeature } from '@openfeature/web-sdk'
import { updateSlotTargeting } from '../utils/ad-ops/adFunctions'
import { preAYScripts } from '../utils/ad-ops/preAYScripts'
import {
  markConsentTiming,
  measureConsentTiming,
} from '../utils/consent/consentTiming'

const logger = debug.withTag('plugins/assertive.client')

function getArticleIdFromRoute(
  route: RouteLocationNormalizedGeneric
): string | null {
  const idParam = route.params.id ?? null
  const id = Array.isArray(idParam) ? idParam[0] : idParam
  return id ?? null
}

export default defineNuxtPlugin({
  name: 'assertive',
  dependsOn: ['openfeature', 'user-segment'],
  parallel: true,
  setup: async (nuxtApp) => {
    markConsentTiming('ay-plugin-start', true)
    const { client } = await useOpenFeature()
    const { track } = useAnalytics()

    // use this to control whether ads are enabled in development
    const { ayEntityId, isDevMode, devDisableAds, devAYTestEntityId } =
      useRuntimeConfig().public

    const layoutName = client.getStringValue('splittestname', 'control')
    const layoutSkeleton = client.getStringValue('layoutSkeleton', 'control')

    const typeTag = await getTypeTag()

    const analyticsProps: AnalyticsProps = {
      ntp_app_version: getNtpAppVersion(),
      host_browser_app_version: await getHostBrowserAppVersion(),
      type_tag: typeTag,
    }

    const openFeatureContext = OpenFeature.getContext()
    const userId =
      typeof openFeatureContext.targetingKey === 'string'
        ? openFeatureContext.targetingKey
        : null

    const ayUserId = userId ? `${userId}-${typeTag.slice(-6)}` : null

    const route = useRoute()
    const initialArticleId = getArticleIdFromRoute(route)
    const pageviewId = getPageviewId()

    // The manager adopts an existing window.ayManagerEnv and drains the queue
    // it finds, so seeding it here means nothing can reach for it too early.
    if (!window.ayManagerEnv) {
      Reflect.set(window, 'ayManagerEnv', { cmd: [] })
    }

    // scriptLoadAY writes window.assertive after mount now, so forward each
    // read to the live object rather than freezing a copy at plugin time.
    const assertiveTarget: Partial<AssertiveYieldOptions> = {}
    const assertive = new Proxy(assertiveTarget, {
      get: (_target, prop) => Reflect.get(window.assertive ?? {}, prop),
    })

    const registerAyHandlers = () => {
      // Refresh article_id before the next accepted pageview is synchronized
      // with AY.
      useRouter().afterEach((to) => {
        const nextArticleId = getArticleIdFromRoute(to)
        window.assertiveQueue = window.assertiveQueue || []
        window.assertiveQueue.push(function () {
          if (window.assertive?.analytics?.custom) {
            window.assertive.analytics.custom.custom_7 = nextArticleId
          }
        })
      })

      // A navigation during the manager download happens before the hook above
      // exists, so set the id the app is actually on. Queued after the one
      // scriptLoadAY pushed, so it wins.
      window.assertiveQueue = window.assertiveQueue || []
      window.assertiveQueue.push(function () {
        if (window.assertive?.analytics?.custom) {
          window.assertive.analytics.custom.custom_7 = getArticleIdFromRoute(
            useRouter().currentRoute.value
          )
        }
      })

      window?.ayManagerEnv?.cmd.push(() => {
        window?.ayManagerEnv?.onEvent(
          'afterRefresh',
          (adUnitInstanceCode: string[]) => {
            adUnitInstanceCode.forEach((code) => {
              const eventData: NtpAdEvent = {
                ntp_tile_position: -1,
                ntp_ad_provider: 'programmatic',
                ntp_creative_id: '',
                ntp_placement_name: stripAYCode(code),
                ntp_ad_brand: '',
                ntp_ad_clickurl: '',
                ntp_ad_refresh: true,
              }
              track('NtpAd_Rendered', eventData)
            })
          }
        )
      })

      // update targeting for each slot -KVs in GAM360 MUCH match
      window?.ayManagerEnv?.cmd.push(() => {
        window?.ayManagerEnv?.onEvent(
          'afterDefineSlot',
          (adUnitInstanceCode: string, slot: Slot) => {
            updateSlotTargeting(adUnitInstanceCode, slot)
          }
        )
      })

      if (!window.analytics) {
        window.analytics = useAnalytics()
      }
    }

    // Same order as before, CMP then manager then GTM, but chained rather than
    // awaited. $ayLoaded is that chain, which the ad slot awaits before it asks.
    const ayLoaded: Promise<boolean> = preAYScripts()
      .then(() => {
        markConsentTiming('ay-request-start', true)
        return scriptLoadAY(
          ayEntityId,
          analyticsProps,
          layoutName,
          layoutSkeleton,
          typeof openFeatureContext.cohort === 'string'
            ? openFeatureContext.cohort
            : null,
          initialArticleId,
          ayUserId,
          isDevMode,
          devDisableAds,
          devAYTestEntityId,
          pageviewId
        )
      })
      .then((loaded) => {
        markConsentTiming('ay-request-settled', true)
        measureConsentTiming(
          'ay-request',
          'ay-request-start',
          'ay-request-settled'
        )
        if (!loaded) {
          logger.error('Assertive Yield SDK failed to load')
          return false
        }
        logger.info('Assertive Yield loaded')
        measureConsentTiming(
          'ay-ready',
          'ay-plugin-start',
          'ay-request-settled'
        )
        registerAyHandlers()
        return true
      })
      .catch((error) => {
        logger.error('Assertive Yield SDK failed to load', error)
        return false
      })

    nuxtApp.provide('ayLoaded', ayLoaded)
    nuxtApp.provide('assertive', assertive)
    nuxtApp.provide('ayManagerEnv', window.ayManagerEnv)
  },
})
